<template>
    <v-card :loading="loading">
        <v-card-title>
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>

            <h3>Record Material Receipt</h3>
        </v-card-title>

        <v-divider class="ml-4 mr-5" />

        <v-card-text class="pt-4">
            <v-row class="mx-0 px-3 py-3 grey lighten-4 rounded">
                <v-col cols="12" md="6" class="py-1">
                    <span class="font-weight-bold">CONTAINER:</span>

                    {{ item.container || '-' }}
                </v-col>

                <v-col cols="12" md="6" class="py-1">
                    <span class="font-weight-bold">Packing List:</span>

                    {{ item.packingListCode || '-' }}
                </v-col>
            </v-row>

            <v-data-table
                :headers="headers"
                :items="tableItems"
                class="elevation-0 mt-4"
                disable-pagination
                hide-default-footer
                :mobile-breakpoint="0"
            >
                <template v-slot:[`item.qtySentToSite`]="{ item: row }">
                    <span>{{ row.qtySentToSite }}</span>
                </template>

                <template v-slot:[`item.alreadyReceived`]="{ item: row }">
                    {{ row.alreadyReceived }}
                </template>

                <template v-slot:[`item.qtyToReceive`]="{ item: row }">
                    <v-text-field
                        v-model.number="row.qtyToReceive"
                        type="number"
                        min="1"
                        :max="row.remainingQty"
                        dense
                        :error-messages="siteQtyError(row)"
                        :hint="siteQtyHint(row)"
                        persistent-hint
                        class="my-1"
                    />
                </template>
            </v-data-table>
        </v-card-text>

        <v-card-actions>
            <v-spacer />

            <v-btn
                rounded
                color="primary"
                class="mx-2 mb-3"
                :disabled="!canSave"
                :loading="loading"
                @click="save"
            >
                Record Material Receipt
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'

import { mapMutations } from 'vuex'

export default {
    name: 'RecordSiteReceipt',

    props: {
        item: {
            type: Object,

            required: true,
        },
    },

    data: () => ({
        loading: false,

        headers: [
            {
                text: 'WORK ORDER',
                value: 'workOrder',
                align: 'center',
                sortable: false,
            },

            {
                text: 'ITEM CODE',
                value: 'itemCode',
                align: 'center',
                sortable: false,
            },

            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },

            {
                text: 'FINISH & UC CODE',
                value: 'finishAndUCCode',
                align: 'center',
                sortable: false,
            },

            {
                text: 'SENT TO SITE QTY',
                value: 'qtySentToSite',
                align: 'center',
                sortable: false,
            },

            {
                text: 'ALREADY RECEIVED',
                value: 'alreadyReceived',
                align: 'center',
                sortable: false,
            },

            {
                text: 'SITE RECEIVED QTY',
                value: 'qtyToReceive',
                align: 'center',
                sortable: false,
                width: '100px',
            },
        ],

        tableItems: [],
    }),

    computed: {
        canSave() {
            const row = this.tableItems[0]

            if (!row) {
                return false
            }

            const qty = Number(row.qtyToReceive)

            const remainingQty = Number(row.remainingQty || 0)

            return qty > 0 && qty <= remainingQty
        },
    },

    created() {
        const qtySentToSite = this.getTotalSentToSite(this.item)
        const alreadyReceived = this.getTotalSiteReceived(this.item)
        const remainingQty = Math.max(qtySentToSite - alreadyReceived, 0)

        this.tableItems = [
            {
                workOrder: this.item.workOrder,
                itemCode: this.item.itemCode,
                description: this.item.description,
                finishAndUCCode: this.item.finishAndUCCode,
                qtySentToSite,
                alreadyReceived,
                remainingQty,
                qtyToReceive: '',
            },
        ]
    },

    methods: {
        ...mapMutations(['setErrorItems']),

        getTotalSentToSite(item) {
            const sentToSite = item.sentToSite || []

            return sentToSite.reduce(
                (sum, entry) => sum + Number(entry.qty || 0),

                0
            )
        },

        getTotalSiteReceived(item) {
            const siteReceipts = item.siteReceipts || []

            if (siteReceipts.length > 0) {
                return siteReceipts.reduce(
                    (sum, receipt) => sum + Number(receipt.qty || 0),

                    0
                )
            }

            return Number(item.qtySiteReceipt || 0)
        },

        siteQtyHint(row) {
            if (Number(row.qtySentToSite || 0) <= 0) {
                return 'No units have been sent to site yet'
            }

            return `Max: ${row.remainingQty} remaining`
        },

        siteQtyError(row) {
            const qty = Number(row.qtyToReceive)

            const maxQty = Number(row.remainingQty || 0)

            if (
                row.qtyToReceive === '' ||
                row.qtyToReceive === null ||
                row.qtyToReceive === undefined
            ) {
                return ''
            }

            if (qty <= 0) {
                return 'Qty must be greater than zero'
            }

            if (qty > maxQty) {
                return `Cannot be greater than remaining qty (${maxQty})`
            }

            return ''
        },

        close() {
            this.$emit('closeDialog')
        },

        async save() {
            try {
                this.loading = true

                const qty = Number(this.tableItems[0].qtyToReceive)

                const packingList = await API.recordPackingItemReceipt({
                    projectId: this.item.projectId,
                    packingListId: this.item.packingListId,
                    packageId: this.item.packageId,
                    itemIndex: this.item.itemIndex,
                    type: 'site',
                    qty,
                })
                this.$emit('saved', { qty, type: 'site', packingList })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,

                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
